import type { NavigationCategory } from '@/lib/use-layout-config';
import type { WmsTemplateCode } from '@/lib/wms-template-config';

export type WarehouseNavigationMode = 'compat' | 'wm-primary' | 'wm-only';
export type WarehouseModernizationStage = 'incubating' | 'pilot' | 'ready';

export interface WarehouseNavigationPolicyConfig {
  mode?: WarehouseNavigationMode;
  wmModernizationStage?: WarehouseModernizationStage;
  preserveLegacyModulesUntilReady?: boolean;
}

interface WarehouseNavigationGovernanceContext {
  enabledModules?: string[];
  warehouseProfileTemplateCode?: string;
  policy?: WarehouseNavigationPolicyConfig;
}

const LEGACY_WAREHOUSE_CATEGORY_IDS = new Set(['wg', 'wp']);
const WM_CATEGORY_ID = 'wm';
const CF_CATEGORY_ID = 'cf';

function hasModule(enabledModules: string[] | undefined, moduleCode: string): boolean {
  return (enabledModules ?? []).some((value) => value.trim().toUpperCase() === moduleCode);
}

function resolveWmLabel(templateCode?: string): string {
  switch (templateCode as WmsTemplateCode | undefined) {
    case 'general-3pl':
      return 'WM Generic Warehouse';
    case 'yard-control':
      return 'WM Yard Control';
    case 'manufacturing-stores':
      return 'WM Manufacturing';
    case 'ecommerce-fulfillment':
      return 'WM E-Commerce Fulfillment';
    case 'distribution-hub':
      return 'WM Distribution Hub';
    case 'hybrid-flex':
      return 'WM Hybrid Warehouse';
    default:
      return 'WM Warehouse';
  }
}

function effectiveMode(context: WarehouseNavigationGovernanceContext): WarehouseNavigationMode {
  const policy = context.policy;
  if (!policy) return 'compat';
  if (policy.preserveLegacyModulesUntilReady && policy.wmModernizationStage !== 'ready') {
    return 'compat';
  }
  if (policy.wmModernizationStage === 'incubating') return 'compat';
  if (policy.wmModernizationStage === 'pilot' && policy.mode === 'wm-only') return 'wm-primary';
  return policy.mode ?? 'compat';
}

function relabelWarehouseCategories(
  navigation: NavigationCategory[],
  mode: WarehouseNavigationMode,
  templateCode?: string,
): NavigationCategory[] {
  return navigation.map((category) => {
    if (category.id === WM_CATEGORY_ID) {
      return { ...category, label: resolveWmLabel(templateCode) };
    }
    if (category.id === 'wg') {
      return { ...category, label: mode === 'compat' ? 'WG 3PL Warehouse' : 'WG 3PL Warehouse (Legacy)' };
    }
    if (category.id === 'wp') {
      return { ...category, label: mode === 'compat' ? 'WP Project Warehouse' : 'WP Project Warehouse (Legacy)' };
    }
    return category;
  });
}

function reprioritizeWarehouseCategories(navigation: NavigationCategory[], priorityOrder: string[]): NavigationCategory[] {
  const family = navigation.filter((category) => priorityOrder.includes(category.id));
  if (family.length <= 1) return navigation;

  const familyOrderMap = new Map(priorityOrder.map((id, index) => [id, index]));
  const baseOrder = Math.min(...family.map((category) => category.order));
  const reprioritized = family
    .slice()
    .sort((left, right) => (familyOrderMap.get(left.id) ?? 99) - (familyOrderMap.get(right.id) ?? 99))
    .map((category, index) => ({
      ...category,
      order: baseOrder + index * 0.01,
    }));
  const reprioritizedMap = new Map(reprioritized.map((category) => [category.id, category]));

  return navigation.map((category) => reprioritizedMap.get(category.id) ?? category);
}

export function applyWarehouseNavigationGovernance(
  navigation: NavigationCategory[],
  context: WarehouseNavigationGovernanceContext,
): NavigationCategory[] {
  const wmEnabled = hasModule(context.enabledModules, 'WM') || hasModule(context.enabledModules, 'WMS');
  const mode = effectiveMode(context);
  let next = relabelWarehouseCategories(navigation, mode, context.warehouseProfileTemplateCode);

  if (!wmEnabled) return next;

  if (mode === 'wm-only') {
    return next
      .filter((category) => !LEGACY_WAREHOUSE_CATEGORY_IDS.has(category.id))
      .sort((left, right) => left.order - right.order);
  }

  if (mode === 'wm-primary') {
    next = reprioritizeWarehouseCategories(next, ['wm', 'wg', 'wp']);
  }

  return next.sort((left, right) => left.order - right.order);
}

export function isWarehouseLegacyCategory(categoryId: string): boolean {
  return LEGACY_WAREHOUSE_CATEGORY_IDS.has(categoryId);
}

export function isWarehouseCanonicalCategory(categoryId: string): boolean {
  return categoryId === WM_CATEGORY_ID;
}

export function isWarehouseIndependentCategory(categoryId: string): boolean {
  return categoryId === CF_CATEGORY_ID;
}
